/**
 * «الرعاية والإرشاد» — school counseling (integration guide §7).
 *
 * SCHOOLS PRODUCT ONLY. The provider is the school's own employee, the time is a
 * school period, and a child's booking may wait on a guardian. Nothing here
 * draws on the session pool the school bought from us.
 */

/** §7.3 lifecycle. Labels arrive from the server — never re-derive them. */
export type OrgSessionStatus = 'pending_guardian' | 'confirmed' | 'cancelled' | 'completed' | 'no_show';

/** `at_school` (بالمدرسة) · `online` (أونلاين) · `with_guardian` (مع ولي الأمر). */
export type OrgSessionLocation = 'at_school' | 'online' | 'with_guardian';

/**
 * `not_required` · `pending`, plus the granted/declined states the consent flow
 * sets (§7.3). Kept open as a string union with a catch-all because the consent
 * flow's own vocabulary (`consent_granted`, …) lands in this same field.
 */
export type OrgGuardianState = 'not_required' | 'pending' | (string & {});

export interface OrgCounselor {
  memberId: number;
  name: string;
  /** «المرشد الطلابي · مدارس …» — present on the options list, absent on a slot's nested counselor. */
  title: string | null;
}

/**
 * A concrete (slot × date) pair, ALREADY sorted by date then time and already
 * minus what is full. TODAY IS DELIBERATELY EXCLUDED (§7.1) — a break that has
 * passed is worse than no offer.
 */
export interface OrgCounselingSlot {
  slotId: number;
  /** `YYYY-MM-DD`. */
  date: string;
  weekday: string;
  /** «الفسحة الأولى» — a school period, not a clock range. */
  timeLabel: string;
  /** `HH:mm`. */
  startTime: string;
  /** Minutes. */
  duration: number;
  location: string;
  locationLabel: string;
  counselor: OrgCounselor | null;
}

export interface OrgGuardianConsentPolicy {
  /**
   * True for the `full` and `summary` tiers (kindergarten, primary, middle),
   * false for `minimal` (high school). Show {@link note} on the booking screen
   * BEFORE the student picks a time (§7.1).
   */
  consentRequired: boolean;
  note: string;
}

export interface OrgCounselingCost {
  free: boolean;
  /** «بلا أي تكلفة عليك» — ready to render. */
  label: string;
}

/**
 * `GET /counseling/options` — ⚠️ TWO DIFFERENT SHAPES (trap 12). Branch on
 * {@link available} / {@link reason} before reading anything else:
 *
 *  · `proxy_stage` (kindergarten) — {@link slots}, {@link guardian}, {@link cost}
 *    and {@link checklist} are MISSING entirely from the payload. Modelled as
 *    empty/null here so a view can render without optional-chaining every field,
 *    but {@link hasBookingShape} is the honest test.
 *  · `no_slots` — `slots: []`, yet guardian/cost/checklist ARE present.
 *  · available — everything present.
 */
export interface OrgCounselingOptions {
  available: boolean;
  /** `proxy_stage` · `no_slots` · null. */
  reason: string | null;
  /** Ready-to-render explanation when unavailable; null when available. */
  message: string | null;
  counselors: readonly OrgCounselor[];
  slots: readonly OrgCounselingSlot[];
  /** Null ONLY in the proxy-stage shape. */
  guardian: OrgGuardianConsentPolicy | null;
  /** Null ONLY in the proxy-stage shape. */
  cost: OrgCounselingCost | null;
  /** Empty in the proxy-stage shape. */
  checklist: readonly string[];
}

export const EMPTY_COUNSELING_OPTIONS: OrgCounselingOptions = {
  available: false,
  reason: null,
  message: null,
  counselors: [],
  slots: [],
  guardian: null,
  cost: null,
  checklist: [],
};

/**
 * Did this payload carry the booking-screen furniture? False for the proxy-stage
 * shape, true for `no_slots` (which still has guardian/cost/checklist) and for
 * the available shape.
 */
export function hasBookingShape(options: OrgCounselingOptions): boolean {
  return options.guardian !== null || options.cost !== null || options.checklist.length > 0;
}

/** The proxy stage: the child has no account, so the meeting is arranged by adults. */
export function isProxyStage(options: OrgCounselingOptions): boolean {
  return options.reason === 'proxy_stage';
}

/** One of the member's own sessions (§7.3). The counselor's clinical note is NEVER returned. */
export interface OrgCounselingSession {
  id: number;
  /** `YYYY-MM-DD`. */
  date: string;
  timeLabel: string;
  /** `HH:mm`. */
  startTime: string;
  location: string;
  locationLabel: string;
  status: string;
  /** Ready to render — «بانتظار موافقة ولي الأمر» · «مؤكّد» · … */
  statusLabel: string;
  guardianState: string;
  counselor: string | null;
  cancelReason: string | null;
}

/** The guardian's view of the same session, plus the child's real name (§8.5). */
export interface OrgGuardianRequest extends OrgCounselingSession {
  /** A guardian knows their own child's name; a pseudonym here would be theatre. */
  child: string;
}

/** Only an OPEN session (pending or confirmed) can be cancelled (§7.4). */
export function isOpenSession(session: OrgCounselingSession): boolean {
  return session.status === 'pending_guardian' || session.status === 'confirmed';
}

/**
 * `POST /counseling/bookings` success — the refreshed list plus a ready-to-render
 * sentence («أرسلنا طلب الموافقة لولي أمرك…» or «تم تأكيد موعدك.»). Show it verbatim.
 */
export interface OrgBookingResult {
  sessions: readonly OrgCounselingSession[];
  message: string | null;
}

/** `POST /counseling/guardian-requests/{id}` success (§8.5). */
export interface OrgGuardianDecisionResult {
  requests: readonly OrgGuardianRequest[];
  message: string | null;
}

/** Max length the server accepts for a cancellation reason (§7.4). */
export const ORG_CANCEL_REASON_MAX = 190;
